import React, { useState, useEffect, useCallback } from 'react';
import { useLabelTemplateStore, LabelTemplate, LABEL_SIZES } from '@/store/label-template.store';

interface TemplateSelectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (template: LabelTemplate) => void;
    selectedCount: number;
}

export const TemplateSelectModal: React.FC<TemplateSelectModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    selectedCount
}) => {
    const { templates, initDefaults } = useLabelTemplateStore();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            // 初始化模板
            Promise.resolve(initDefaults()).finally(() => {
                setIsLoading(false);
            });
        }
    }, [isOpen, initDefaults]);

    if (!isOpen) return null;

    const handleConfirm = useCallback(() => {
        const template = templates.find(t => t.id === selectedTemplateId);
        if (template) {
            onSelect(template);
        }
    }, [templates, selectedTemplateId, onSelect]);

    // 按尺寸分组模板，使用完整的类型注解
    const templatesBySize = templates.reduce<Record<string, LabelTemplate[]>>(
        (acc, template) => {
            if (!acc[template.size]) {
                acc[template.size] = [];
            }
            acc[template.size].push(template);
            return acc;
        },
        {}
    );

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-lg bg-white dark:bg-[#1c2433] rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex-none px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">选择标签模板</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                将为 {selectedCount} 个项目生成标签PDF
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-500 dark:text-slate-400 transition-colors"
                        >
                            <span className="material-icons">close</span>
                        </button>
                    </div>
                </div>

                {/* Template List */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {isLoading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin">
                                <span className="material-icons text-4xl text-slate-400">hourglass_empty</span>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 mt-4">加载模板中...</p>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="text-center py-12">
                            <span className="material-icons text-6xl text-slate-300 dark:text-slate-600 mb-4">label_off</span>
                            <p className="text-slate-500 dark:text-slate-400">暂无可用模板</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">请先在设置中创建标签模板</p>
                        </div>
                    ) : (
                        Object.entries(templatesBySize).map(([size, sizeTemplates]) => (
                            <div key={size} className="space-y-2">
                                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    {LABEL_SIZES[size as keyof typeof LABEL_SIZES]?.label || size}
                                </h3>
                                <div className="space-y-2">
                                    {sizeTemplates.map((template) => (
                                        <button
                                            key={template.id}
                                            onClick={() => setSelectedTemplateId(template.id)}
                                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                selectedTemplateId === template.id
                                                    ? 'border-primary bg-primary/5 shadow-md'
                                                    : 'border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                        selectedTemplateId === template.id
                                                            ? 'bg-primary text-white'
                                                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                                    }`}>
                                                        <span className="material-icons text-xl">label</span>
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-slate-900 dark:text-white">
                                                                {template.name}
                                                            </p>
                                                            {template.isDefault && (
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-bold">
                                                                    默认
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                            {template.elements.length} 个元素
                                                        </p>
                                                    </div>
                                                </div>
                                                {selectedTemplateId === template.id && (
                                                    <span className="material-icons text-primary">check_circle</span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="flex-none px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 py-3 rounded-xl font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        >
                            取消
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={!selectedTemplateId}
                            className="flex-1 py-3 rounded-xl font-semibold bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <span className="material-icons text-lg">picture_as_pdf</span>
                            确认导出
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
